import { VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Divider, Radio, Select } from "antd"
import { type RadioChangeEvent } from "antd/lib/radio"
import { type OptionProps } from "antd/lib/select"
import { t } from "i18next"
import React, { useState } from "react"
import { type FormProps } from "~Components/form/form.types"
import { CreditorSelectOptionLabel } from "~Components/forms/expenseForm/commonInputFields/creditorOptionLabel"
import { ALL_TRIGGER_OBJECTS } from "~Shared/config/consts"
import { useFetchVendorDropdown } from "~Shared/hooks/useVendorDropdown.hooks"
import { type CreditorDropdown, type UserCreditorDropDown, type VendorDropdown, defaultQueryParams } from "~Shared/queries/vendors/consts"
import { mapUserCreditorToSelectOption, mapVendorToSelectOption, useManyVendors } from "~Shared/queries/vendors/vendor.queries"
import { doFilterSelect } from "~Shared/utils/helper.utils"
import { type UserCreditor } from "../../../../shared/utils/vendor.utils"
import { InfiniteScrollSelect, type InfiniteScrollSelectInterface } from "../../../form/InfiniteScrollSelect"

interface VendorSelectProps extends Pick<InfiniteScrollSelectInterface<CreditorDropdown>, "isLoading" | "disabled" | "className" | "popupClassName" | "isForm"> {
    shouldShowOnlyVerifiedVendors: boolean
    shouldRenderSelectDropdown: boolean
    selectedVendorType: VendorTypeEnum
    userCreditors?: Array<UserCreditor>
    selectedCreditorId?: string
    selectedCreditorIds?: Array<string>
    fallback?: boolean | string
    fallbackValue?: string | number | null | undefined
    defaultNonSelectedValue?: boolean
    mode?: "multiple" | "tags"
    formProps?: FormProps
    onSelect?: (creditorId: string, vendorType: VendorTypeEnum, vendorName: string) => void
    onSelectMultiple?: (value: Array<string>) => void
    valuePropName?: keyof UserCreditorDropDown & keyof VendorDropdown
}

export const VendorSelect = ({
    shouldShowOnlyVerifiedVendors,
    userCreditors,
    shouldRenderSelectDropdown,
    selectedVendorType,
    selectedCreditorId,
    selectedCreditorIds,
    disabled,
    className,
    popupClassName,
    fallback,
    fallbackValue,
    defaultNonSelectedValue,
    mode,
    isForm,
    isLoading,
    formProps,
    onSelect,
    onSelectMultiple,
    valuePropName,
}: VendorSelectProps) => {
    const [vendorType, setVendorType] = useState<VendorTypeEnum>(selectedVendorType)
    const [
        {
            searchString,
            useInfiniteQueryResult: { fetchNextPage, hasNextPage, isFetchingNextPage, status, fetchStatus, data: vendorCreditors },
        },
        { setSearchString },
    ] = useFetchVendorDropdown({ searchTerm: defaultQueryParams.search, dropdownType: vendorType })

    const vendorDropdownData = vendorCreditors?.pages.flat()
    const userCreditorDropdownData = userCreditors?.map(mapUserCreditorToSelectOption)

    const loadedCreditorIds = [...(userCreditorDropdownData?.map((item) => item.id) ?? []), ...(vendorDropdownData?.map((item) => item.id) ?? [])]
    const cleanSelectedCreditorIds = selectedCreditorIds ?? (selectedCreditorId ? [selectedCreditorId!] : [])
    const selectedVendorIdsWithMissingData = cleanSelectedCreditorIds.filter((id) => !loadedCreditorIds.includes(id))

    const dropdownData = vendorType === VendorTypeEnum.VENDOR ? vendorDropdownData : userCreditorDropdownData

    const { data: vendors } = useManyVendors(selectedVendorIdsWithMissingData)
    const selectedVendors = vendors?.length ? vendors.map(mapVendorToSelectOption) : undefined

    const fallbackText = fallback && fallback !== true ? fallback : t("input:filter_form.not_set")

    const handleDropdownTypeChange = ({ target: { value } }: RadioChangeEvent) => {
        setVendorType(value)
    }

    const shouldShowCreditorDropdownData = (creditorDropdownData: CreditorDropdown) => {
        if (vendorType === VendorTypeEnum.VENDOR) {
            return (creditorDropdownData as VendorDropdown)?.isApproved || !shouldShowOnlyVerifiedVendors
        }

        return true
    }

    const renderCreditorSelectOptions = (creditor: CreditorDropdown, index: number) => {
        if (!shouldShowCreditorDropdownData(creditor)) {
            return null
        }
        const value = valuePropName ? (creditor[valuePropName] ?? creditor.id) : creditor.id
        return (
            <Select.Option key={index} value={value} label={creditor.name} id={`vendor-option-${creditor.id}`}>
                <CreditorSelectOptionLabel creditor={creditor} />
            </Select.Option>
        )
    }

    const shouldShowSelectedCreditorOption = selectedVendors?.length && !searchString

    const renderCreditorSelectedOption = () => {
        if (!shouldShowSelectedCreditorOption) {
            return null
        }
        return selectedVendors.map((selectedVendor) => {
            const value = valuePropName ? (selectedVendor[valuePropName] ?? selectedVendor.id) : selectedVendor.id
            return (
                <Select.Option
                    key={selectedVendor.id}
                    value={value}
                    label={selectedVendor.name}
                    id={`item-option-${selectedVendor.id}`}
                    // BUG-10587 - In cases of vendor being selected, but the dropdown type is employee, we want the selected vendor to be displayed properly.
                    // We hide it here to display it as selected, but not to show it on the employee dropdown list.
                    className={vendorType === VendorTypeEnum.USER ? "hidden" : ""}
                >
                    <CreditorSelectOptionLabel creditor={selectedVendor} />
                </Select.Option>
            )
        })
    }

    const shouldShowFallbackOption = fallback && mode !== "tags" && !searchString

    const renderFallbackSelectOption = () =>
        shouldShowFallbackOption ? (
            <Select.Option value={fallbackValue ?? ""} label={fallbackText}>
                {fallbackText}
            </Select.Option>
        ) : null

    const shouldShowDefaultNonSelectedValue = defaultNonSelectedValue && !searchString

    const renderDefaultNonSelectedValue = () =>
        shouldShowDefaultNonSelectedValue ? (
            <Select.Option value="" disabled>
                <div className="flex items-center overflow-hidden">
                    <span>-</span>
                </div>
            </Select.Option>
        ) : null

    const renderDropdown = (menu: React.ReactElement) => (
        <div className="card-modal mx-10 mt-5">
            <Radio.Group value={vendorType} onChange={handleDropdownTypeChange} name="dropdownType">
                <Radio.Button key={VendorTypeEnum.VENDOR} value={VendorTypeEnum.VENDOR}>
                    <p className="truncate">{t("label:vendors")}</p>
                </Radio.Button>
                <Radio.Button key={VendorTypeEnum.USER} value={VendorTypeEnum.USER}>
                    <p className="truncate">{t("label:employees")}</p>
                </Radio.Button>
            </Radio.Group>
            <Divider style={{ margin: "8px 0" }} />
            {menu}
        </div>
    )

    const handleChange = (creditorIds: string | Array<string>) => {
        if (mode === "multiple" && Array.isArray(creditorIds)) {
            const lastAddedValue = creditorIds[creditorIds.length - 1]
            const referenceIds = lastAddedValue === ALL_TRIGGER_OBJECTS ? [ALL_TRIGGER_OBJECTS] : creditorIds.filter((id: string) => id !== ALL_TRIGGER_OBJECTS)
            onSelectMultiple?.(referenceIds)
        }
    }

    return (
        <InfiniteScrollSelect<CreditorDropdown>
            dropdownData={dropdownData}
            infiniteScrollData={{ fetchNextPage, fetchStatus, isFetchingNextPage, status, hasNextPage }}
            shouldApplyFilterOption={vendorType === VendorTypeEnum.USER}
            isScrollable={vendorType === VendorTypeEnum.VENDOR}
            selectedItemId={selectedCreditorId}
            doFilterSelect={doFilterSelect}
            setSearchString={setSearchString}
            disabled={disabled}
            className={className}
            popupClassName={popupClassName}
            isForm={isForm}
            isLoading={isLoading}
            mode={mode}
            placeholder={t("placeholder:vendor.select_a_vendor")}
            formProps={formProps}
            dropdownRender={shouldRenderSelectDropdown ? renderDropdown : undefined}
            onSelect={(creditorId: string, option: OptionProps) => onSelect?.(creditorId, vendorType, option.label)}
            onChange={handleChange}
            renderSelectOptions={renderCreditorSelectOptions}
            renderSelectedOption={renderCreditorSelectedOption}
            renderFallbackSelectOption={renderFallbackSelectOption}
            renderDefaultNonSelectedValue={renderDefaultNonSelectedValue}
        />
    )
}
